import { CommonModule } from '@angular/common';
import { Component, computed, inject, Type } from '@angular/core';
import { MDDropDownComponent, MDIconComponent, MDHeaderComponent, MDDialogService, MDToolTipDirective, MDContentBoxComponent } from '@melodic-ui/components';
import { LessonDetailStateService } from '../lesson-detail-state.service';
import { LessonDetailFormStateService } from '../lesson-detail-form-state.service';
import { GradingTemplateCategoryType } from '@lb/api/core/interfaces';
import { ICoreGrade } from '@lb/api/core/interfaces';
import { ViewRubricDialogComponent, toRubricTabsFromCategories } from '../view-rubric-dialog/view-rubric-dialog.component';
import { LoaderComponent } from '@lb/components/loader/loader.component';
import { GradeTagComponent } from '../../grade-tag/grade-tag.component';

@Component({
	selector: 'lb-lesson-detail-daily-grades',
	templateUrl: './lesson-detail-daily-grades.component.html',
	styleUrl: './lesson-detail-daily-grades.component.scss',
	imports: [
		CommonModule,
		MDIconComponent,
		MDHeaderComponent,
		MDDropDownComponent,
		MDToolTipDirective,
		LoaderComponent,
		GradeTagComponent,
		MDContentBoxComponent
	],
	standalone: true
})
export class LessonDetailDailyGradesComponent {
	readonly state = inject(LessonDetailStateService);
	private readonly formState = inject(LessonDetailFormStateService);
	private readonly dialogService = inject(MDDialogService);

	private readonly _templateCategories = this.state.gradingTemplateCategories;
	readonly gradingTemplateLoading = this.state.gradingTemplateLoading;

	readonly categoryGrades = this.formState.categoryGrades;
	readonly isReadOnly = this.formState.isReadOnly;
	readonly fieldsDisabled = this.formState.fieldsDisabled;

	perLessonCategories = computed(() => {
		return this._templateCategories().filter((c) => c.categoryType !== GradingTemplateCategoryType.Project);
	});

	/**
	 * Whether a project category exists on the template. The rubric dialog only lists the per-lesson
	 * categories, so its weights stop short of 100% — this decides whether we can name the project as
	 * where the rest of the grade lives, or have to stay generic.
	 */
	private readonly hasProjectCategory = computed(() =>
		this._templateCategories().some((c) => c.categoryType === GradingTemplateCategoryType.Project)
	);

	categoryGradeOptions = this.state.gradeOptions;
	categoryGradeOptionsWithoutNoGrade = computed(() => this.categoryGradeOptions().filter((option) => option.value !== null));

	/**
	 * Returns true if ANY required category is invalid (attempted submit + missing grade)
	 * Used to set content-box type to 'error' for red styling
	 */
	hasInvalidCategories = computed(() => {
		return this.perLessonCategories().some((c) => this.formState.isCategoryInvalid(c.gradingTemplateCategoryID));
	});

	onGradeChange(categoryID: number, gradeID: unknown): void {
		if (typeof gradeID === 'number' || gradeID === null) {
			this.formState.updateCategoryGrade(categoryID, gradeID as number | null);
		}
	}

	getCategoryGradeOptions(categoryType: number) {
		return categoryType === GradingTemplateCategoryType.Homework ? this.categoryGradeOptions() : this.categoryGradeOptionsWithoutNoGrade();
	}

	openRubricDialog(): void {
		this.dialogService.open(ViewRubricDialogComponent as Type<any>, {
			tabs: toRubricTabsFromCategories(this.perLessonCategories()),
			tabNoun: 'category',
			tabNounPlural: 'categories',
			// These tabs exclude the project category (it isn't graded per lesson), so the weights shown
			// here stop short of 100%. The dialog works the shortfall out from the totals; we only name
			// the project as the cause when the template actually has one.
			unlistedWeightLabel: this.hasProjectCategory()
				? 'the project, which is graded on its own criteria at the end of the semester'
				: 'grading categories not shown here'
		});
	}

	getGrade(gradeID: number | null): ICoreGrade | null {
		return this.state.grades().find((g) => g.gradeID === gradeID) ?? null;
	}
}
